/**
 * Typed-ish wrapper over the Express API.
 *
 * Access tokens are short-lived; a 401 triggers one silent refresh and a
 * single retry. If that fails the tokens are cleared and `rh:signed-out` is
 * dispatched, which <App> listens for to bounce back to the login screen.
 */
const BASE = import.meta.env.VITE_API_BASE || '/api';

let access = localStorage.getItem('rh_token') || '';
let refresh = localStorage.getItem('rh_refresh') || '';

export function setTokens(t, r) {
  if (t) { access = t; localStorage.setItem('rh_token', t); }
  if (r) { refresh = r; localStorage.setItem('rh_refresh', r); }
  watchExpiry();
}

export function clearTokens() {
  access = '';
  refresh = '';
  clearTimeout(expiryTimer);
  localStorage.removeItem('rh_token');
  localStorage.removeItem('rh_refresh');
}

/*
 * The session ends when the access token does — the server gives the refresh
 * token the same expiry, so past that point there is nothing left to refresh
 * with. Watching the clock rather than waiting for the next request means an
 * idle tab returns to the sign-in screen at the same moment an active one
 * does, instead of sitting on a dead dashboard until something is clicked.
 */
let expiryTimer = null;

/** What the token says about its holder, or null when there is none to read. */
function claimsOf(token) {
  try {
    /* JWT payloads are base64url, and atob only reads plain base64. */
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64)) || null;
  } catch {
    return null;
  }
}

/** When the token stops being valid, in ms since the epoch; 0 if unreadable. */
function expiryOf(token) {
  const { exp } = claimsOf(token) || {};
  return exp ? exp * 1000 : 0;
}

/**
 * Who is signed in, as far as the token says — `{ id, role, name }`, or null.
 *
 * Read from the token rather than from /auth/me because the few places that
 * want it (a browser copy of somebody's own preferences, kept apart from the
 * next person's on a shared machine) need it before, and without, that call.
 */
export function sessionUser() {
  const c = access ? claimsOf(access) : null;
  return c && c.sub ? { id: c.sub, role: c.role || '', name: c.name || '' } : null;
}

function endSession() {
  clearTokens();
  window.dispatchEvent(new CustomEvent('rh:signed-out'));
}

function watchExpiry() {
  clearTimeout(expiryTimer);
  if (!access) return;
  const exp = expiryOf(access);
  if (!exp) return; /* Unreadable, so let the next 401 settle it instead. */
  const left = exp - Date.now();
  if (left <= 0) { endSession(); return; }
  expiryTimer = setTimeout(endSession, left);
}

/* A reload lands mid-session, so pick the countdown back up. */
watchExpiry();

export const hasSession = () => !!access;

/**
 * Fetch bytes rather than JSON.
 *
 * `raw` parses every answer as JSON, which an image is not. This is the same
 * request with the same bearer token, handed back as a Blob so the caller can
 * make an object URL of it.
 */
async function bytes(path) {
  const res = await fetch(BASE + path, {
    headers: access ? { authorization: `Bearer ${access}` } : {}
  });
  if (!res.ok) throw new Error(`Could not load ${path}`);
  return res.blob();
}

/**
 * Post JSON and get a file back.
 *
 * `raw` reads every answer as JSON, which a spreadsheet is not. Same bearer
 * token, same error shape, handed back as a Blob for download().
 */
async function postBytes(path, body) {
  const res = await fetch(BASE + path, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(access ? { authorization: `Bearer ${access}` } : {})
    },
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = (await res.json()).error || msg; } catch { /* not JSON */ }
    /* The status rides along, as it does on `raw`'s errors: a caller can
       tell "this server has no such route yet" (404) from "you may not"
       (403) and fall back on the first without swallowing the second. */
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

async function raw(method, path, body, retry = true) {
  const res = await fetch(BASE + path, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(access ? { authorization: `Bearer ${access}` } : {})
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (res.status === 401 && retry && refresh) {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh })
    });
    if (r.ok) {
      setTokens((await r.json()).token);
      return raw(method, path, body, false);
    }
    clearTokens();
    window.dispatchEvent(new CustomEvent('rh:signed-out'));
    throw new Error('Your session ended — sign in again');
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = new Error((data && data.error) || res.statusText);
    err.status = res.status;
    err.detail = data && data.detail;
    throw err;
  }
  return data;
}

const get = p => raw('GET', p);
const post = (p, b) => raw('POST', p, b || {});
const patch = (p, b) => raw('PATCH', p, b);
const put = (p, b) => raw('PUT', p, b);
const del = p => raw('DELETE', p);

const qs = o => {
  const s = new URLSearchParams(
    Object.entries(o || {}).filter(([, v]) => v !== undefined && v !== ''));
  const t = s.toString();
  return t ? `?${t}` : '';
};

const resource = name => ({
  list: params => get(`/${name}${qs(params)}`),
  get: id => get(`/${name}/${id}`),
  create: body => post(`/${name}`, body),
  update: (id, body) => patch(`/${name}/${id}`, body),
  remove: id => del(`/${name}/${id}`)
});

export const api = {
  base: BASE,
  raw,
  setTokens,
  clearTokens,
  hasSession,

  sessionUser,

  /*
   * The signed-in person's own preferences: a JSON value per key, such as
   * a table's column layout under 'grid:pipeline'. `value` comes back null
   * when nothing has been saved under the key. Per user, so a layout
   * follows its owner to any device rather than being the workspace's.
   */
  me: {
    prefs: {
      get: key => get(`/me/prefs/${encodeURIComponent(key)}`),
      put: (key, value) => put(`/me/prefs/${encodeURIComponent(key)}`, { value })
    }
  },

  /*
   * Any table as a file: the columns (key, label, type) and the rows keyed
   * by column, laid out by the server as CSV or an Excel workbook. The rows
   * are sent rather than a query, so what comes out is exactly the view in
   * front of whoever pressed the button. Needs the `export` permission.
   */
  exportTable: body => postBytes('/export', body),

  auth: {
    register: b => post('/auth/register', b).then(r => (setTokens(r.token, r.refresh), r)),
    login: (email, password) =>
      post('/auth/login', { email, password }).then(r => (setTokens(r.token, r.refresh), r)),
    logout: () => post('/auth/logout', { refresh }).finally(clearTokens),
    me: () => get('/auth/me'),
    /* Your own name, title, department and number - and nothing else about
       the account, which is somebody else's decision. */
    updateMe: b => patch('/auth/me', b),
    users: () => get('/auth/users'),
    invite: (email, role) => post('/auth/invite', { email, role }),
    accept: b => post('/auth/accept', b).then(r => (setTokens(r.token, r.refresh), r)),
    createUser: b => post('/auth/users', b),
    updateUser: (id, b) => patch(`/auth/users/${id}`, b),
    deleteUser: id => del(`/auth/users/${id}`),
    /* The menu tree, the verbs each menu supports, and the policy in force. */
    access: () => get('/auth/access'),
    saveAccess: roles => put('/auth/access', { access: roles }),
    addRole: name => post('/auth/roles', { name }),
    deleteRole: name => del('/auth/roles/' + encodeURIComponent(name)),
    changePassword: (current, next) => post('/auth/password', { current, next })
  },

  clients: {
    ...resource('clients'),
    view: () => get('/view/clients'),
    pay: (id, body) => post(`/clients/${id}/pay`, body || {}),
    payments: id => get(`/clients/${id}/payments`),
    timeline: id => get(`/clients/${id}/timeline`),
    /* Every email, WhatsApp, push and logged call for one client. The address
       and phone travel as query params because a product-API client has no
       local row to read them from, and the comms log keys on them, not on id. */
    comms: (id, { email, phone } = {}) => {
      const qs = new URLSearchParams();
      if (email) qs.set('email', email);
      if (phone) qs.set('phone', phone);
      const tail = qs.toString();
      return get(`/clients/${id}/comms${tail ? `?${tail}` : ''}`);
    },
    statement: id => get(`/clients/${id}/statement`),
    importCSV: csv => post('/clients/import', { csv }),
    exportURL: () => `${BASE}/clients/export.csv`,
    /* The client report: the plans, the promo codes, who has paid and who is
       using it. Both files are built from the payload the screen sends, so
       neither can disagree with what was on the page when the button went. */
    reportBook: payload => postBytes('/client-report.xlsx', payload),
    reportPdf: payload => postBytes('/client-report.pdf', payload)
  },
  deals: {
    ...resource('deals'),
    /* A string is the old shorthand (just the stage); an object carries the
     Move-stage dialog's comment, next step and the `require` flag that makes
     the server enforce the comment. */
  stage: (id, body) => post(`/deals/${id}/stage`, typeof body === 'string' ? { stage: body } : body),
    promote: (id, value) => post(`/deals/${id}/promote`, { value }),
    /* `from` says which board carried it across. A lead can go straight onto
       the ledger without ever being a deal, and the client record should say
       which of the two happened. */
    convert: (id, from) => post(`/deals/${id}/convert`, { from }),
    /* Before moving anybody: are they already a customer? */
    clientCheck: id => get(`/deals/${id}/client-check`),
    /* They said no. The note is required — see the route. */
    drop: (id, body) => post(`/deals/${id}/drop`, body),
    undrop: id => post(`/deals/${id}/undrop`),
    /* What happened to it, append-only. The stage trail used to be
       activity rows, which put the history where anybody tidying up
       could delete it. */
    events: id => get(`/deals/${id}/events`),
    logEvent: (id, body) => post(`/deals/${id}/events`, body),
    /* The list as it is on screen, laid out by the server - same workbook
       styling as the demo report, so the two exports cannot drift. */
    book: payload => postBytes('/deals.xlsx', payload),
    /* The rows on screen, laid out for paper. The sales report is a whole
       document and has its own pair under `sales`. */
    reportPdf: payload => postBytes('/list-report.pdf', payload)
  },
  /* The sales report: what came in, and what became of it. Both files
     are built from the payload the screen sends, so neither can disagree
     with what was on the page when the button was pressed. */
  sales: {
    reportBook: payload => postBytes('/sales-report.xlsx', payload),
    reportPdf: payload => postBytes('/sales-report.pdf', payload)
  },
  /* Tracked demo bookings, read through from the visitor tracker. Read-only:
     there is no create/update/delete, because the tracker owns them. */
  leads: {
    list: days => get(days ? `/leads?days=${days}` : '/leads'),
    sites: () => get('/leads/sites'),
    /* What the tracker can tell apart: the ten capture types, the two states,
       how long it keeps sessions, and which form fields survive the
       allowlist. Read once, so the filters are not a second copy of a list
       the server already owns. */
    meta: () => get('/leads/meta'),
    /*
     * Pull from the tracker now, rather than waiting for the minute tick.
     *
     * The server polls every minute over a one-day window, so a new enquiry
     * normally appears on its own. This is for the other case: somebody has
     * just been told a lead came in and wants to see it, and "wait up to a
     * minute" is not an answer to give them while they are looking at the
     * screen.
     *
     * Safe to press twice — the sync matches existing rows rather than
     * creating them again — but it is not quick: the tracker takes fifteen
     * to twenty seconds to answer, so whatever calls this has to say so.
     */
    sync: days => post('/tracker/sync', days ? { days } : {}),
    /* What a sync would do, writing nothing. */
    preview: () => get('/tracker/sync')
  },
  /* Demo bookings: Calendly's slots, and the work done on them (attendance,
     outcome, follow-up, who it belongs to) which is ours. */
  calendly: {
    bookings: opts => get(opts && opts.refresh ? '/calendly/bookings?refresh=1' : '/calendly/bookings'),
    update: (id, data) => patch(`/calendly/bookings/${id}`, data),
    /* A meeting that never went through Calendly — arranged by phone, held
       on whatever call suited. Only these can be removed: a Calendly one
       would be written back by the next sync. */
    addManual: body => post('/calendly/bookings/manual', body),
    removeBooking: id => del(`/calendly/bookings/${id}`),
    restoreBooking: id => post(`/calendly/bookings/${id}/restore`),
    /* A partner who booked through the customer link. One way only —
       the meeting decides the rest. */
    markB2B: id => patch(`/calendly/bookings/${id}`, { customerType: 'B2B' }),
    link: (id, data) => post(`/calendly/bookings/${id}/link`, data || {}),
    prospect: id => post(`/calendly/bookings/${id}/prospect`),
    /* Correct one, matched on the stamp it carries — see the route. */
    editRound: (id, body) => post(`/calendly/bookings/${id}/feedback/edit`, body),
    /* Take one back off, matched on the stamp it carries — see the route. */
    removeRound: (id, at) => post(`/calendly/bookings/${id}/feedback/remove`, { at }),
    feedback: (id, body) => post(`/calendly/bookings/${id}/feedback`,
      typeof body === 'string' ? { text: body } : body),
    followUp: (id, body) => post(`/calendly/bookings/${id}/followup`, body),
    reportBook: report => postBytes('/calendly/report.xlsx', report),
    reportCsv: report => postBytes('/calendly/report.csv', report),
    reportPdf: report => postBytes('/calendly/report.pdf', report),
    assignees: () => get('/calendly/assignees'),
    addAssignee: name => post('/calendly/assignees', { name }),
    renameAssignee: (id, name) => patch(`/calendly/assignees/${id}`, { name }),
    setAssigneeActive: (id, active) => patch(`/calendly/assignees/${id}`, { active }),
    removeAssignee: id => del(`/calendly/assignees/${id}`),
    pipeline: id => post(`/calendly/bookings/${id}/pipeline`),
    sync: () => post('/calendly/sync')
  },
  contacts: resource('contacts'),
  companies: resource('companies'),
  activities: resource('activities'),
  outbox: resource('outbox'),
  rules: resource('rules'),

  alerts: {
    list: () => get('/alerts'),
    read: key => post(`/alerts/${encodeURIComponent(key)}/read`),
    snooze: (key, days) => post(`/alerts/${encodeURIComponent(key)}/snooze`, { days: days || 3 }),
    dismiss: key => post(`/alerts/${encodeURIComponent(key)}/dismiss`),
    restore: key => post(`/alerts/${encodeURIComponent(key)}/restore`),
    readAll: () => post('/alerts/read-all')
  },

  reports: {
    summary: () => get('/reports/summary'),
    revenue: months => get(`/reports/revenue${qs({ months })}`),
    pipeline: () => get('/reports/pipeline'),
    conversion: () => get('/reports/conversion')
  },

  settings: {
    get: () => get('/settings'),
    save: body => raw('PUT', '/settings', body)
  },

  marketing: {
    get: () => get('/marketing'),
    save: data => raw('PUT', '/marketing', { data }),
    send: body => post('/marketing/send', body),
    senders: () => get('/marketing/senders'),
    verify: body => post('/marketing/verify', body),
    sheet: body => post('/marketing/sheet', body),
    asset: body => post('/marketing/assets', body),
    assetBlob: id => bytes(`/marketing/assets/${id}`),
    replies: () => get('/marketing/replies'),
    events: () => get('/marketing/events'),
    checkReplies: () => post('/marketing/replies/check', {}),
    // Scheduled sends: the messages go up whole, and come back as a log.
    schedule: body => post('/marketing/schedule', body),
    unschedule: campaignId => post('/marketing/schedule/cancel', { campaignId }),
    queue: () => get('/marketing/queue')
  },

  whatsapp: {
    templates: () => get('/whatsapp/templates'),
    // The Chatlio connection: settings, a test that sends nothing, and the
    // server-owned log of everything sent and received.
    setup: () => get('/whatsapp/setup'),
    saveSetup: body => raw('PUT', '/whatsapp/setup', body),
    testSetup: body => post('/whatsapp/setup/test', body || {}),
    testMeta: body => post('/whatsapp/setup/test-meta', body || {}),
    messages: () => get('/whatsapp/messages')
  },

  push: {
    setup: () => get('/push/setup'),
    saveSetup: body => raw('PUT', '/push/setup', body),
    testSetup: body => post('/push/setup/test', body || {}),
    send: body => post('/push/send', body),
    messages: () => get('/push/messages'),
    source: () => get('/push/source'),
    saveSource: body => raw('PUT', '/push/source', body),
    testSource: body => post('/push/source/test', body || {}),
    audience: () => get('/push/audience')
  },

  mail: {
    get: () => get('/mail'),
    save: body => raw('PUT', '/mail', body),
    test: body => post('/mail/test', body),
    imapTest: body => post('/mail/imap-test', body)
  },

  /*
   * URL Tracking.
   *
   * Nothing here is cached in the app store the way clients and deals are.
   * Click counts move on their own, without anybody in this tab doing
   * anything, so a copy held in memory would be wrong within seconds — these
   * screens ask the server each time and poll for the parts that tick.
   */
  urlTracking: {
    campaigns: {
      list: params => get(`/url-tracking/campaigns${qs(params)}`),
      get: id => get(`/url-tracking/campaigns/${id}`),
      create: body => post('/url-tracking/campaigns', body),
      update: (id, body) => patch(`/url-tracking/campaigns/${id}`, body),
      /* Archives; the server keeps the row and its clicks. `hard` really
         deletes, and is only offered for a campaign that never ran. */
      remove: (id, hard) => del(`/url-tracking/campaigns/${id}${hard ? '?hard=true' : ''}`)
    },
    links: {
      list: params => get(`/url-tracking/links${qs(params)}`),
      get: id => get(`/url-tracking/links/${id}`),
      create: body => post('/url-tracking/links', body),
      update: (id, body) => patch(`/url-tracking/links/${id}`, body),
      remove: (id, hard) => del(`/url-tracking/links/${id}${hard ? '?hard=true' : ''}`),
      pause: id => post(`/url-tracking/links/${id}/pause`),
      activate: id => post(`/url-tracking/links/${id}/activate`),
      /* One link per recipient, copied from this one. Returns what was made,
         who already had one, and which ids could not be found. */
      personalise: (id, clientIds) => post(`/url-tracking/links/${id}/personalise`, { clientIds })
    },
    analytics: {
      dashboard: params => get(`/url-tracking/analytics${qs(params)}`),
      /* Just the counters — the fallback when the live stream is unavailable. */
      live: params => get(`/url-tracking/analytics/live${qs(params)}`),
      campaign: (id, params) => get(`/url-tracking/analytics/campaign/${id}${qs(params)}`),
      link: (id, params) => get(`/url-tracking/analytics/link/${id}${qs(params)}`),
      by: (dimension, params) => get(`/url-tracking/analytics/${dimension}${qs(params)}`),
      /* Who was sent a personalised link and who acted on it. */
      recipients: params => get(`/url-tracking/analytics/recipients${qs(params)}`),
      /* Both exports come back as bytes rather than JSON, and both need the
         bearer token — which is why they are fetched rather than linked. */
      exportCSV: params => bytes(`/url-tracking/analytics/export.csv${qs(params)}`),
      exportLinks: () => bytes('/url-tracking/links/export.csv')
    },

    /*
     * The live click stream.
     *
     * EventSource cannot carry an Authorization header, so the session is
     * exchanged for a single-use ticket first and that is what goes in the URL.
     * The ticket is good for thirty seconds and buys nothing but this feed.
     */
    /* How location and time-on-page are resolved, plus the beacon snippet
       with this deployment's own address already in it. */
    setup: () => get('/url-tracking/setup'),

    streamTicket: () => post('/url-tracking/stream-ticket'),
    streamUrl: ticket => `${BASE}/url-tracking/stream?ticket=${encodeURIComponent(ticket)}`
  },

  tools: {
    run: name => post(`/tools/${name}`),
    audit: limit => get(`/audit${qs({ limit })}`)
  },

  ai: {
    chat: messages => post('/ai/chat', { messages }),
    brief: () => post('/ai/brief'),
    digest: () => post('/ai/digest'),
    salesBrief: () => post('/ai/sales-brief'),
    analyse: () => post('/ai/analyse'),
    scoreClients: () => post('/ai/score/clients'),
    scoreStatus: id => get(`/ai/score/clients/${id}`),
    scoreDeals: () => post('/ai/score/deals'),
    draft: body => post('/ai/draft', body),
    /* One record read properly. `module` says which builder on the server
       gathers the facts; the answer comes back in one shape whichever it is. */
    insight: (module, id) => post('/ai/insight', { module, id }),
    search: q => post('/ai/search', { q }),
    usage: () => get('/ai/usage')
  },

  health: () => fetch('/healthz').then(r => r.json())
};

export default api;
